import type { ApprovalItem, ChangeRequest, Holder } from '../types';

export function buildApprovals(holders: Holder[]): ApprovalItem[] {
  const someone = holders.find(
    (holder) => holder.tenantId === 't-fides' && holder.type === 'COMPANY',
  )!;
  return [
    {
      id: 'ap-1',
      kind: 'DISTRIBUTION',
      refId: 'ds-opp-q1',
      fundId: 'f-opp',
      title: 'OPP1 September quarter distribution',
      summary: '2.5 cents per paid unit, payable 28 Oct 2026. ABA file generated on approval.',
      requestedBy: 'Alex Morgan',
      requestedAt: '2026-10-15T16:20:00',
      status: 'PENDING',
    },
    {
      id: 'ap-2',
      kind: 'UNIT_ISSUE',
      refId: 'app-5',
      fundId: 'f-sdf2',
      title: 'Issue units: Kestrel Trust, $750,000 SDF2',
      summary: 'Funds received 20 Sep and matched. KYC verified; wholesale certificate on file.',
      amount: 750_000,
      requestedBy: 'Alex Morgan',
      requestedAt: '2026-10-14T11:05:00',
      status: 'PENDING',
    },
    {
      id: 'ap-3',
      kind: 'BANK_CHANGE',
      refId: 'cr-2',
      title: `Bank account change: ${someone.name}`,
      summary:
        'Requested via the investor portal with MFA. Call-back verification completed 15 Oct.',
      requestedBy: 'Investor portal',
      requestedAt: '2026-10-15T09:12:00',
      status: 'PENDING',
      changes: [
        { field: 'BSB', from: someone.bank.bsb, to: '063-000' },
        { field: 'Account', from: `•••• ${someone.bank.accountNumber.slice(-3)}`, to: '•••• 771' },
      ],
    },
    {
      id: 'ap-4',
      kind: 'CAPITAL_CALL',
      refId: 'cc-opp-3',
      fundId: 'f-opp',
      title: 'OPP1 Call 3 (20%)',
      summary: 'Call notices issued 1 Oct, due 24 Oct.',
      requestedBy: 'Alex Morgan',
      requestedAt: '2026-09-29T15:00:00',
      status: 'APPROVED',
      decidedBy: 'Priya Nair',
      decidedAt: '2026-09-30T10:30:00',
      comment: 'Matches the IC-approved drawdown schedule.',
    },
    {
      id: 'ap-5',
      kind: 'DISTRIBUTION',
      refId: 'ds-mez-q1',
      fundId: 'f-mez',
      title: 'MEZ2 September quarter distribution',
      summary: '3.0 cents per unit.',
      requestedBy: 'Alex Morgan',
      requestedAt: '2026-10-08T14:00:00',
      status: 'APPROVED',
      decidedBy: 'Priya Nair',
      decidedAt: '2026-10-09T09:10:00',
    },
    {
      id: 'ap-6',
      kind: 'MIGRATION',
      refId: 'mg-1',
      title: 'Commit migration: 4 funds from Registry Direct',
      summary: 'Opening balances as at 30 Jun 2026, reconciled to the cent.',
      requestedBy: 'Alex Morgan',
      requestedAt: '2026-06-30T17:00:00',
      status: 'APPROVED',
      decidedBy: 'Priya Nair',
      decidedAt: '2026-07-01T08:30:00',
    },
  ];
}

export function buildChangeRequests(holders: Holder[]): ChangeRequest[] {
  const fides = holders.filter((holder) => holder.tenantId === 't-fides');
  const company = fides.find((holder) => holder.type === 'COMPANY')!;
  const [first, second, third, fourth] = fides.slice(20, 24);
  return [
    {
      id: 'cr-1',
      reference: 'CR-26-0311',
      holderId: first.id,
      kind: 'TRANSFER',
      status: 'VERIFYING',
      channel: 'EMAIL',
      submittedAt: '2026-10-13',
      fundId: 'f-psd',
      classId: 'c-psd-a',
      units: 100_000,
      toHolderId: second.id,
      details: [
        { label: 'Transfer type', value: 'Off-market, related party (estate planning)' },
        { label: 'Consideration', value: 'Nil' },
      ],
      verification: ['Signed transfer form received', 'Transferee wholesale status: pending'],
    },
    {
      id: 'cr-2',
      reference: 'CR-26-0314',
      holderId: company.id,
      kind: 'BANK_CHANGE',
      status: 'PENDING_APPROVAL',
      channel: 'INVESTOR_PORTAL',
      submittedAt: '2026-10-15',
      details: [
        { label: 'New BSB', value: '063-000' },
        { label: 'New account', value: '•••• 771' },
      ],
      verification: ['MFA step-up passed', 'Call-back to registered phone completed'],
    },
    {
      id: 'cr-3',
      reference: 'CR-26-0309',
      holderId: third.id,
      kind: 'ADDRESS_CHANGE',
      status: 'COMPLETED',
      channel: 'INVESTOR_PORTAL',
      submittedAt: '2026-10-07',
      details: [{ label: 'New address', value: '44 Cotham Road, Kew VIC 3101' }],
      verification: ['MFA step-up passed'],
    },
    {
      id: 'cr-4',
      reference: 'CR-26-0315',
      holderId: fourth.id,
      kind: 'REDEMPTION',
      status: 'SUBMITTED',
      channel: 'INVESTOR_PORTAL',
      submittedAt: '2026-10-15',
      fundId: 'f-core',
      classId: 'c-core-a',
      units: 50_000,
      details: [
        { label: 'Redemption window', value: 'Quarterly, 31 Dec 2026' },
        { label: 'Price', value: 'NAV at window date' },
      ],
      verification: ['MFA step-up passed'],
    },
    {
      id: 'cr-5',
      reference: 'CR-26-0302',
      holderId: 'h-bennett-ind',
      kind: 'DRP_ELECTION',
      status: 'COMPLETED',
      channel: 'INVESTOR_PORTAL',
      submittedAt: '2026-09-02',
      fundId: 'f-core',
      details: [{ label: 'Election', value: 'Reinvest 100% of CORE distributions' }],
      verification: ['MFA step-up passed'],
    },
    {
      id: 'cr-6',
      reference: 'CR-26-0298',
      holderId: second.id,
      kind: 'TRANSMISSION',
      status: 'VERIFYING',
      channel: 'EMAIL',
      submittedAt: '2026-08-28',
      details: [
        { label: 'Deceased', value: 'Joint holder' },
        { label: 'Documents', value: 'Death certificate received; awaiting probate' },
      ],
      verification: ['Certified death certificate', 'Probate: outstanding'],
    },
  ];
}
